import React, { useState } from 'react';
import { searchBookingRequestByRequestId } from '../../services/bookingRequestsService';
import './CustomerTracking.css';

const CustomerTracking = () => {
  const [lrNumber, setLrNumber] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState('');
  const [expandedStages, setExpandedStages] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Format date from API (ISO format) to readable format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  // Map booking request data to tracking timeline format
  const mapBookingRequestToTrackingData = (apiRequest) => {
    // Extract request data from response
    let requestData = apiRequest;
    if (apiRequest.data) {
      requestData = apiRequest.data.request || apiRequest.data.booking_request || apiRequest.data;
    } else if (apiRequest.request || apiRequest.booking_request) {
      requestData = apiRequest.request || apiRequest.booking_request;
    }

    if (!requestData) {
      throw new Error('Invalid booking request data');
    }

    // Map tracking status fields
    const mapTrackingStatus = () => {
      // Check for direct tracking fields (snake_case or camelCase)
      const startReached = requestData.start_location_reached !== undefined ? requestData.start_location_reached :
                          requestData.startLocationReached !== undefined ? requestData.startLocationReached :
                          requestData.start_location_reached_at ? true : false;
      
      const startDeparted = requestData.start_location_departed !== undefined ? requestData.start_location_departed :
                           requestData.startLocationDeparted !== undefined ? requestData.startLocationDeparted :
                           requestData.start_location_departed_at ? true : false;
      
      const destReached = requestData.destination_reached !== undefined ? requestData.destination_reached :
                         requestData.destinationReached !== undefined ? requestData.destinationReached :
                         requestData.destination_reached_at ? true : false;

      return {
        startLocationReached: !!startReached,
        startLocationReachedTime: requestData.start_location_reached_at || requestData.startLocationReachedAt || null,
        startLocationDeparted: !!startDeparted,
        startLocationDepartedTime: requestData.start_location_departed_at || requestData.startLocationDepartedAt || null,
        destinationReached: !!destReached,
        destinationReachedTime: requestData.destination_reached_at || requestData.destinationReachedAt || null
      };
    };

    const trackingStatus = mapTrackingStatus();

    // Determine overall status - Check for Cancelled/Rejected first
    let overallStatus = 'Placed';
    let statusMessage = 'Your order has been placed and confirmed.';
    
    // Handle Cancelled and Rejected statuses
    if (requestData.status === 'Cancelled') {
      overallStatus = 'Cancelled';
      statusMessage = 'Your booking has been cancelled.';
    } else if (requestData.status === 'Rejected') {
      overallStatus = 'Rejected';
      statusMessage = 'Your booking request has been rejected.';
    } else if (trackingStatus.destinationReached) {
      overallStatus = 'Reached';
      statusMessage = 'Your shipment has been delivered successfully.';
    } else if (trackingStatus.startLocationDeparted) {
      overallStatus = 'In Transit';
      statusMessage = 'Your shipment is currently in transit and is expected to be delivered on time.';
    } else if (trackingStatus.startLocationReached) {
      overallStatus = 'Dispatched';
      statusMessage = 'Your shipment has been dispatched and is on its way.';
    } else if (requestData.status === 'Confirmed') {
      overallStatus = 'Confirmed';
      statusMessage = 'Your booking has been confirmed.';
    } else if (requestData.status === 'Contacted') {
      overallStatus = 'Contacted';
      statusMessage = 'We have contacted you regarding your booking.';
    }

    // Build timeline - Handle Cancelled/Rejected statuses differently
    const timeline = [];

    // If cancelled or rejected, show simplified timeline
    if (requestData.status === 'Cancelled' || requestData.status === 'Rejected') {
      timeline.push({
        stage: 'Placed',
        location: requestData.from_location || 'Origin',
        description: 'Order has been placed and confirmed',
        dateTime: formatDate(requestData.created_at || requestData.requested_date),
        status: 'completed',
        icon: '📦'
      });
      
      timeline.push({
        stage: requestData.status === 'Cancelled' ? 'Cancelled' : 'Rejected',
        location: requestData.status === 'Cancelled' ? 'Booking Cancelled' : 'Booking Rejected',
        description: requestData.status === 'Cancelled' 
          ? 'This booking has been cancelled'
          : 'This booking request has been rejected',
        dateTime: formatDate(requestData.updated_at || requestData.created_at),
        status: 'completed',
        icon: requestData.status === 'Cancelled' ? '❌' : '🚫'
      });
      
      return {
        lrNumber: requestData.request_id || `BR-${requestData.id}`,
        origin: requestData.from_location || 'Origin',
        destination: requestData.to_location || 'Destination',
        estimatedDelivery: 'N/A',
        status: overallStatus,
        statusMessage: statusMessage,
        timeline: timeline
      };
    }

    // Build timeline - Always show all 4 stages like in the first image
    // Stage 1: Placed (Always completed)
    timeline.push({
      stage: 'Placed',
      location: requestData.from_location || 'Origin',
      description: 'Order has been placed and confirmed',
      dateTime: formatDate(requestData.created_at || requestData.requested_date),
      status: 'completed',
      icon: '📦'
    });

    // Stage 2: Dispatched (Always show, completed or pending)
    const dispatchedLocation = trackingStatus.startLocationDeparted 
      ? (requestData.from_location || 'Origin')
      : (requestData.from_location || 'Origin');
    
    timeline.push({
      stage: 'Dispatched',
      location: dispatchedLocation,
      description: trackingStatus.startLocationDeparted 
        ? 'Shipment has left the origin facility'
        : 'Shipment will be dispatched from the origin facility',
      dateTime: trackingStatus.startLocationDeparted 
        ? formatDate(trackingStatus.startLocationDepartedTime)
        : '',
      status: trackingStatus.startLocationDeparted ? 'completed' : 'pending',
      icon: '🚚'
    });

    // Stage 3: In Transit (Always show, with intermediate stops if available)
    const stops = requestData.stops || [];
    const reachedStops = stops.filter((stop, index) => {
      const stopObj = typeof stop === 'object' ? stop : { location: stop };
      return stopObj.reached || stopObj.reached_at || stopObj.actual_arrival_time;
    });

    // Build intermediate stops array
    const intermediateStops = stops.length > 0 ? stops.map((stop, index) => {
      const stopObj = typeof stop === 'object' ? stop : { location: stop };
      const reached = stopObj.reached || stopObj.reached_at || stopObj.actual_arrival_time;
      
      return {
        location: stopObj.location || stopObj.stop_location || stop || `Stop ${index + 1}`,
        dateTime: formatDate(stopObj.reached_at || stopObj.actual_arrival_time),
        description: reached ? 'Shipment arrived at intermediate stop' : 'Shipment will arrive at intermediate stop'
      };
    }).filter(stop => stop.location) : [];

    // Determine In Transit location and status
    let inTransitLocation = 'En Route';
    let inTransitDateTime = '';
    let inTransitStatus = 'pending';

    if (trackingStatus.startLocationDeparted) {
      if (reachedStops.length > 0) {
        // Use last reached stop location
        const lastStop = reachedStops[reachedStops.length - 1];
        const lastStopObj = typeof lastStop === 'object' ? lastStop : { location: lastStop };
        inTransitLocation = lastStopObj.location || lastStopObj.stop_location || lastStop || 'En Route';
        inTransitDateTime = formatDate(lastStopObj.reached_at || lastStopObj.actual_arrival_time);
      } else if (stops.length > 0) {
        // Use first stop location if not reached yet
        const firstStop = stops[0];
        const firstStopObj = typeof firstStop === 'object' ? firstStop : { location: firstStop };
        inTransitLocation = firstStopObj.location || firstStopObj.stop_location || firstStop || 'En Route';
      } else {
        // No stops, use departed time
        inTransitLocation = 'En Route';
        inTransitDateTime = formatDate(trackingStatus.startLocationDepartedTime);
      }
      
      if (!trackingStatus.destinationReached) {
        inTransitStatus = 'in-progress';
      }
    }

    // Add In Transit stage
    const inTransitStage = {
      stage: 'In Transit',
      location: inTransitLocation,
      description: 'Shipment is in transit to the destination',
      dateTime: inTransitDateTime,
      status: inTransitStatus,
      icon: '⏰'
    };

    // Add intermediate stops if available
    if (intermediateStops.length > 0) {
      inTransitStage.intermediateStops = intermediateStops;
    }

    timeline.push(inTransitStage);

    // Stage 4: Reached/Delivered (Always show, completed or pending)
    timeline.push({
      stage: 'Reached',
      location: requestData.to_location || 'Destination',
      description: trackingStatus.destinationReached
        ? 'Shipment has been delivered to the recipient'
        : 'Shipment will be delivered to the recipient',
      dateTime: trackingStatus.destinationReached 
        ? formatDate(trackingStatus.destinationReachedTime)
        : '',
      status: trackingStatus.destinationReached ? 'completed' : 'pending',
      icon: '✓'
    });

    // Calculate estimated delivery (if available)
    const estimatedDelivery = requestData.requested_date 
      ? formatDate(requestData.requested_date).split(',')[0] 
      : 'TBD';

    return {
      lrNumber: requestData.request_id || `BR-${requestData.id}`,
      origin: requestData.from_location || 'Origin',
      destination: requestData.to_location || 'Destination',
      estimatedDelivery: estimatedDelivery,
      status: overallStatus,
      statusMessage: statusMessage,
      timeline: timeline
    };
  };

  const handleTrack = async () => {
    if (!lrNumber.trim()) {
      setError('Please enter a request ID or LR number');
      return;
    }

    setIsLoading(true);
    setError('');
    setTrackingData(null);

    try {
      const searchQuery = lrNumber.trim().toUpperCase();
      const response = await searchBookingRequestByRequestId(searchQuery);
      
      // Map the booking request to tracking data format
      const mappedData = mapBookingRequestToTrackingData(response);
      setTrackingData(mappedData);
      setError('');
    } catch (err) {
      console.error('Error tracking shipment:', err);
      setTrackingData(null);
      setError(err.message || 'Request ID or LR number not found. Please check and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTrack();
    }
  };

  const toggleStageExpansion = (index) => {
    setExpandedStages(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Placed':
      case 'Dispatched':
        return '#10B981'; // Green
      case 'In Transit':
        return '#3B82F6'; // Blue
      case 'Reached':
        return '#6B7280'; // Grey
      default:
        return '#6B7280';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'Placed':
      case 'Dispatched':
      case 'Confirmed':
      case 'Contacted':
        return '#10B981'; // Green
      case 'In Transit':
        return '#6366F1'; // Purple/Indigo
      case 'Cancelled':
        return '#EF4444'; // Red
      case 'Rejected':
        return '#F59E0B'; // Orange/Amber
      case 'Reached':
        return '#10B981'; // Green
      default:
        return '#6B7280'; // Grey
    }
  };

  return (
    <div className="customer-tracking-container">
      <div className="customer-tracking-header">
        <h1 className="customer-tracking-title">Track Your Shipment</h1>
        <p className="customer-tracking-subtitle">Enter your Request ID (BR-XXXX) or LR number to track your shipment status</p>
      </div>

      <div className="customer-tracking-search-section">
        <div className="customer-tracking-search-box">
          <div className="customer-search-icon">🔍</div>
          <input
            type="text"
            className="customer-tracking-input"
            placeholder="Enter Request ID (e.g., BR-2025-001) or LR Number"
            value={lrNumber}
            onChange={(e) => setLrNumber(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <button 
            className="customer-track-now-btn" 
            onClick={handleTrack}
            disabled={isLoading}
          >
            {isLoading ? 'Tracking...' : 'Track Now'}
          </button>
        </div>
        {error && <div className="customer-tracking-error">{error}</div>}
      </div>

      {trackingData && (
        <>
          <div className="customer-tracking-summary-card">
            <div className="customer-summary-row">
              <div className="customer-summary-item">
                <span className="customer-summary-label">LR Number:</span>
                <span className="customer-summary-value">{trackingData.lrNumber}</span>
              </div>
              <div className="customer-summary-item">
                <span className="customer-summary-label">Origin:</span>
                <span className="customer-summary-value">{trackingData.origin}</span>
              </div>
              <div className="customer-summary-item">
                <span className="customer-summary-label">Destination:</span>
                <span className="customer-summary-value">{trackingData.destination}</span>
              </div>
              <div className="customer-summary-item">
                <span className="customer-summary-label">Estimated Delivery:</span>
                <span className="customer-summary-value">{trackingData.estimatedDelivery}</span>
              </div>
            </div>
          </div>

          <div className="customer-status-banner" style={{ backgroundColor: getStatusBgColor(trackingData.status) }}>
            <span className="customer-status-text">{trackingData.status}</span>
            <span className="customer-status-message">{trackingData.statusMessage}</span>
          </div>

          <div className="customer-tracking-details-section">
            <h2 className="customer-tracking-details-title">Tracking Details</h2>
            <div className="customer-timeline-container">
              {trackingData.timeline.map((item, index) => {
                const isCompleted = item.status === 'completed';
                const isInProgress = item.status === 'in-progress';
                const isPending = item.status === 'pending';
                const isLast = index === trackingData.timeline.length - 1;
                
                // Check if next stage is completed (for line color)
                const nextStage = !isLast ? trackingData.timeline[index + 1] : null;
                const nextStageCompleted = nextStage && nextStage.status === 'completed';
                const nextStageInProgress = nextStage && nextStage.status === 'in-progress';

                const hasIntermediateStops = item.intermediateStops && item.intermediateStops.length > 0;
                const isExpanded = expandedStages[index];

                return (
                  <div key={index} className="customer-timeline-item">
                    <div className="customer-timeline-content">
                      <div className="customer-timeline-icon-wrapper" style={{ 
                        backgroundColor: isCompleted ? '#10B981' : isInProgress ? '#3B82F6' : '#E5E7EB',
                        color: isCompleted || isInProgress ? 'white' : '#9CA3AF'
                      }}>
                        {isCompleted ? (
                          <div className="customer-icon-package">{item.icon}</div>
                        ) : isInProgress ? (
                          <div className="customer-icon-clock">{item.icon}</div>
                        ) : (
                          <div className="customer-icon-checkmark">{item.icon}</div>
                        )}
                      </div>
                      <div className="customer-timeline-details">
                        <div className="customer-timeline-stage-header">
                          <div>
                            <div 
                              className="customer-timeline-stage"
                              style={{ 
                                color: isCompleted && item.stage === 'Reached' ? '#10B981' : 
                                       isCompleted ? '#10B981' : 
                                       isInProgress ? '#3B82F6' : '#1F2937'
                              }}
                            >
                              {item.stage}
                            </div>
                            <div className="customer-timeline-location">{item.location}</div>
                            <div className="customer-timeline-description">{item.description}</div>
                            {item.dateTime && (
                              <div className="customer-timeline-datetime">{item.dateTime}</div>
                            )}
                            {!item.dateTime && isPending && (
                              <div className="customer-timeline-pending">Pending</div>
                            )}
                          </div>
                          {hasIntermediateStops && (
                            <button 
                              className="customer-expand-toggle-btn"
                              onClick={() => toggleStageExpansion(index)}
                              aria-label={isExpanded ? 'Collapse stops' : 'Expand stops'}
                            >
                              <svg 
                                width="20" 
                                height="20" 
                                viewBox="0 0 20 20" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ 
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.3s ease'
                                }}
                              >
                                <path 
                                  d="M5 7.5L10 12.5L15 7.5" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                        {hasIntermediateStops && isExpanded && (
                          <div className="customer-intermediate-stops-container">
                            <div className="customer-intermediate-stops-label">Intermediate Stops:</div>
                            {item.intermediateStops.map((stop, stopIndex) => (
                              <div key={stopIndex} className="customer-intermediate-stop-item">
                                <div className="customer-intermediate-stop-dot" />
                                <div className="customer-intermediate-stop-details">
                                  <div className="customer-intermediate-stop-location">{stop.location}</div>
                                  <div className="customer-intermediate-stop-description">{stop.description}</div>
                                  <div className="customer-intermediate-stop-datetime">{stop.dateTime}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {!isLast && (
                      <div 
                        className="customer-timeline-line" 
                        style={{ 
                          // Line should be green if current stage is completed/in-progress OR next stage is completed
                          backgroundColor: (isCompleted || isInProgress || nextStageCompleted || nextStageInProgress) ? '#10B981' : '#E5E7EB',
                          height: hasIntermediateStops && isExpanded ? 'auto' : '60px',
                          minHeight: '60px'
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerTracking;

